import React, { useState, useEffect } from 'react';
import { SearchBar, StatCard, DataTable, Button, Badge } from '../../../components/ui';
import { Settings, CheckCircle, AlertCircle, Clock, Plus, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../../services/api';
import SupportTicketModal from '../components/SupportTicketModal';
import toast from 'react-hot-toast';

export default function TechnicalSupportScreen() {
  const [searchTerm, setSearchTerm] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await api.get('/support?type=Technical');
      if (res.data.success) {
        setTickets(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch technical support tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleEditClick = (ticket) => {
    setEditingTicket(ticket);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingTicket(null);
  };

  const handleModalSuccess = (updatedTicket) => {
    if (editingTicket) {
      setTickets(tickets.map(t => t._id === updatedTicket._id ? updatedTicket : t));
    } else {
      setTickets([updatedTicket, ...tickets]);
    }
  };

  const filteredTickets = tickets.filter(t => 
    t.subject?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    t.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.createdBy?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { header: 'Ticket ID', accessor: (row) => <span className="text-[13px] text-slate-700">{row.ticketId}</span>, minWidth: '100px' },
    { header: 'Subject', accessor: (row) => <span className="text-[13px] text-slate-800">{row.subject}</span>, minWidth: '250px' },
    { header: 'Created By', accessor: (row) => <span className="text-[13px] text-slate-600">{row.createdBy}</span>, minWidth: '120px' },
    { header: 'Date', accessor: (row) => <span className="text-xs text-slate-500">{format(new Date(row.createdAt), 'dd MMM yyyy, HH:mm')}</span>, minWidth: '150px' },
    { 
      header: 'Priority', 
      accessor: (row) => (
        <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-md ${
          row.priority === 'high' ? 'bg-red-100 text-red-700' :
          row.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
        }`}>
          {row.priority || 'medium'}
        </span>
      ), 
      minWidth: '100px' 
    },
    { header: 'Status', accessor: (row) => <Badge status={row.status} />, minWidth: '120px' },
    { 
      header: 'Actions', 
      accessor: (row) => (
        <button 
          onClick={() => handleEditClick(row)}
          className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
          title="Edit Ticket"
        >
          <Pencil size={16} />
        </button>
      ), 
      minWidth: '80px' 
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Technical Support</h1>
          <p className="text-sm text-slate-500 mt-1">Manage internal technical issues and platform support</p>
        </div>
        <Button className="flex items-center gap-2" onClick={() => { setEditingTicket(null); setIsModalOpen(true); }}>
          <Plus size={16} /> New Ticket
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          variant="luxury"
          title="Total Tickets"
          value={tickets.length}
          sub="All time"
          iconHoverColor="group-hover:text-purple-500"
          topColor="border-t-purple-500"
          illustration={<Settings className="w-10 h-10 text-slate-300" strokeWidth={1.5} />}
        />
        <StatCard
          variant="luxury"
          title="Open Tickets"
          value={tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length}
          sub="Requires attention"
          iconHoverColor="group-hover:text-rose-500"
          topColor="border-t-rose-500"
          illustration={<AlertCircle className="w-10 h-10 text-slate-300" strokeWidth={1.5} />}
        />
        <StatCard
          variant="luxury"
          title="Recent"
          value={tickets.filter(t => {
            const today = new Date();
            const ticketDate = new Date(t.createdAt);
            return (today - ticketDate) / (1000 * 60 * 60 * 24) < 7;
          }).length}
          sub="Last 7 days"
          iconHoverColor="group-hover:text-blue-500"
          topColor="border-t-blue-500"
          illustration={<Clock className="w-10 h-10 text-slate-300" strokeWidth={1.5} />}
        />
        <StatCard
          variant="luxury"
          title="Resolved"
          value={tickets.filter(t => t.status === 'resolved').length}
          sub="Successfully fixed"
          iconHoverColor="group-hover:text-emerald-500"
          topColor="border-t-emerald-500"
          illustration={<CheckCircle className="w-10 h-10 text-slate-300" strokeWidth={1.5} />}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col h-[500px]">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-xl">
          <SearchBar 
            value={searchTerm} 
            onChange={setSearchTerm} 
            placeholder="Search tickets by ID, Subject, or Creator..." 
            className="w-full max-w-sm"
          />
        </div>
        <div className="flex-1 overflow-hidden p-0 relative">
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-500">Loading...</div>
          ) : (
            <DataTable columns={columns} data={filteredTickets} />
          )}
        </div>
      </div>

      <SupportTicketModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        type="Technical"
        initialData={editingTicket}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
